import React from 'react';
import { TrendingUp, TrendingDown, Award, Users, Target, BarChart3, Activity, ArrowUp, ArrowDown, Minus } from 'lucide-react';

// Calculate percentile position
const getPercentile = (score, industryAvg, topPerformer) => {
  // Simple percentile calculation based on score position
  const range = topPerformer - 1; // Range from 1 to top performer
  const position = score - 1; // Position from minimum
  const percentile = Math.round((position / range) * 100);
  return Math.min(99, Math.max(1, percentile));
};

// Get gap analysis
const getGapAnalysis = (score, benchmark, label) => {
  const gap = benchmark - score;
  if (gap <= 0) return { status: 'above', gap: Math.abs(gap), label };
  if (gap < 0.5) return { status: 'close', gap, label };
  return { status: 'below', gap, label };
};

const DataMaturityComparison = ({ 
  overallScore, 
  industryBenchmark, 
  topPerformerScore, 
  dimensionScores, 
  industry,
  companyName 
}) => {
  const percentile = getPercentile(overallScore, industryBenchmark, topPerformerScore);
  const medianScore = (industryBenchmark + 1) / 2 + 1; // Approximate median
  
  // Gap analysis
  const gapFromAvg = getGapAnalysis(overallScore, industryBenchmark, 'Industry Average');
  const gapFromTop = getGapAnalysis(overallScore, topPerformerScore, 'Top Performers');
  
  // Find strengths and weaknesses
  const sortedDimensions = [...(dimensionScores || [])].sort((a, b) => b.score - a.score);
  const strengths = sortedDimensions.filter(d => d.gap_from_benchmark <= 0).slice(0, 3);
  const weaknesses = sortedDimensions.filter(d => d.gap_from_benchmark > 0.3);
  
  // Percentile color
  const getPercentileColor = (p) => {
    if (p >= 75) return 'text-emerald-600';
    if (p >= 50) return 'text-blue-600';
    if (p >= 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPercentileBg = (p) => {
    if (p >= 75) return 'bg-emerald-50 border-emerald-200';
    if (p >= 50) return 'bg-blue-50 border-blue-200';
    if (p >= 25) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getPercentileLabel = (p) => {
    if (p >= 90) return 'Industry Leader';
    if (p >= 75) return 'Above Average';
    if (p >= 50) return 'Average';
    if (p >= 25) return 'Below Average';
    return 'Needs Improvement';
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="data-maturity-comparison">
      <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
        <Users className="w-6 h-6 text-indigo-600" />
        Industry Benchmark Comparison
      </h2>
      <p className="text-gray-500 text-sm mb-6">See how {companyName} compares against {industry} peers</p>

      {/* Percentile Position Card */}
      <div className={`p-6 rounded-xl border-2 mb-6 ${getPercentileBg(percentile)}`}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center bg-white shadow-lg ${getPercentileColor(percentile)}`}>
              <span className="text-3xl font-bold">{percentile}</span>
              <span className="text-sm">%</span>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Your Percentile Ranking</p>
              <p className={`text-xl font-bold ${getPercentileColor(percentile)}`}>{getPercentileLabel(percentile)}</p>
              <p className="text-gray-500 text-sm">Among {industry} companies</p>
            </div>
          </div>
          
          {/* Visual Position Bar */}
          <div className="w-full md:w-64">
            <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-red-400 via-yellow-400 to-emerald-400"
                style={{ width: '100%' }}
              />
              <div 
                className="absolute top-0 h-full w-1 bg-gray-900 transform -translate-x-1/2"
                style={{ left: `${percentile}%` }}
              />
              <div 
                className="absolute -top-6 transform -translate-x-1/2 text-xs font-bold"
                style={{ left: `${percentile}%` }}
              >
                You
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Bottom</span>
              <span>Average</span>
              <span>Top</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gap Analysis Cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {/* vs Industry Average */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">vs Industry Average</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-blue-600">{industryBenchmark.toFixed(1)}</span>
            <span className="text-gray-500">/ 5.0</span>
          </div>
          <div className={`flex items-center gap-1 mt-2 text-sm ${
            gapFromAvg.status === 'above' ? 'text-emerald-600' : 
            gapFromAvg.status === 'close' ? 'text-yellow-600' : 'text-red-600'
          }`}>
            {gapFromAvg.status === 'above' ? (
              <>
                <ArrowUp className="w-4 h-4" />
                <span>+{gapFromAvg.gap.toFixed(1)} above average</span>
              </>
            ) : gapFromAvg.status === 'close' ? (
              <>
                <Minus className="w-4 h-4" />
                <span>{gapFromAvg.gap.toFixed(1)} gap (within range)</span>
              </>
            ) : (
              <>
                <ArrowDown className="w-4 h-4" />
                <span>-{gapFromAvg.gap.toFixed(1)} below average</span>
              </>
            )}
          </div>
        </div>

        {/* vs Top Performers */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-purple-800">vs Top Performers</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-purple-600">{topPerformerScore.toFixed(1)}</span>
            <span className="text-gray-500">/ 5.0</span>
          </div>
          <div className={`flex items-center gap-1 mt-2 text-sm ${
            gapFromTop.status === 'above' ? 'text-emerald-600' : 'text-purple-600'
          }`}>
            {gapFromTop.status === 'above' ? (
              <>
                <Award className="w-4 h-4" />
                <span>Top performer level!</span>
              </>
            ) : (
              <>
                <Target className="w-4 h-4" />
                <span>{gapFromTop.gap.toFixed(1)} gap to close</span>
              </>
            )}
          </div>
        </div>

        {/* Your Score */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-emerald-600" />
            <span className="font-medium text-emerald-800">Your Score</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-emerald-600">{overallScore.toFixed(1)}</span>
            <span className="text-gray-500">/ 5.0</span>
          </div>
          <div className="flex items-center gap-1 mt-2 text-sm text-emerald-700">
            <TrendingUp className="w-4 h-4" />
            <span>Assessed on {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Dimension Radar Comparison */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Strengths */}
        <div className="p-5 rounded-xl bg-emerald-50 border border-emerald-200">
          <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Your Strengths ({strengths.length})
          </h3>
          {strengths.length > 0 ? (
            <div className="space-y-2">
              {strengths.map((dim, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 bg-white rounded-lg">
                  <span className="text-sm font-medium text-gray-700">{dim.dimension_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-600 font-bold">{dim.score.toFixed(1)}</span>
                    <span className="text-xs text-emerald-500 bg-emerald-100 px-2 py-0.5 rounded-full">
                      +{Math.abs(dim.gap_from_benchmark).toFixed(1)} above
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-emerald-700 text-sm">Building strengths - focus on quick wins first</p>
          )}
        </div>

        {/* Areas for Improvement */}
        <div className="p-5 rounded-xl bg-amber-50 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
            <TrendingDown className="w-5 h-5" />
            Priority Improvement Areas ({weaknesses.length})
          </h3>
          {weaknesses.length > 0 ? (
            <div className="space-y-2">
              {weaknesses.map((dim, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 bg-white rounded-lg">
                  <span className="text-sm font-medium text-gray-700">{dim.dimension_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-amber-600 font-bold">{dim.score.toFixed(1)}</span>
                    <span className="text-xs text-red-500 bg-red-100 px-2 py-0.5 rounded-full">
                      -{dim.gap_from_benchmark.toFixed(1)} gap
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-amber-700 text-sm">Great job! All dimensions at or above benchmark</p>
          )}
        </div>
      </div>

      {/* Anonymous Peer Comparison Note */}
      <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-gray-600 text-sm flex items-start gap-2">
          <Users className="w-4 h-4 mt-0.5 text-gray-400" />
          <span>
            Comparison based on anonymized data from organizations in the <strong>{industry}</strong> sector. 
            Percentile ranking represents your position among peer companies who have completed this assessment.
          </span>
        </p>
      </div>
    </div>
  );
};

export default DataMaturityComparison;
